/**
 * @file VFS.js
 * @description Production-grade Virtual File System with non-blocking initialization.
 * @pattern Hybrid eager/lazy mounting to resolve Interaction to Next Paint (INP) bottlenecks.
 */

export class VFS {
    /**
     * @param {Object} terminalVfsData - Static directory structure (welcome.txt, etc)
     * @param {Object} contentManager - Manager for dynamic blog/project data
     */
    constructor(terminalVfsData, contentManager) { 
        this.currentDirectory = '/home/user';
        this.contentManager = contentManager; 
        
        // 1. Initial State: Deep clone the static manifest immediately
        this.vfs = JSON.parse(JSON.stringify(terminalVfsData));

        // 2. ✅ SENIOR PATTERN: Non-blocking bootstrap
        // Yields to the main thread so the Terminal UI can paint its prompt 
        // before the background content is mounted.
        setTimeout(() => {
            this.mountDynamicContent();
        }, 0);
    }

    // ========================================================
    // BOOTSTRAP LOGIC (ASYNCHRONOUS)
    // ========================================================

    /**
     * Orchestrates the mounting of dynamic content into the directory tree.
     */
    mountDynamicContent() {
        try {
            const projectsNodePath = '/home/user/projects';
            const blogsNodePath = '/home/user/blog';

            // Ensure structural nodes and parent links exist
            this._ensureDirectoryNode(projectsNodePath, 'projects');
            this._ensureDirectoryNode(blogsNodePath, 'blog');

            // Process heavy collections
            this._mountCollection(this.contentManager.getProjects(), projectsNodePath, 'project');
            this._mountCollection(this.contentManager.getBlogPosts(), blogsNodePath, 'blog');
            
            console.log('[VFS] Dynamic content background mounting complete.');
        } catch (error) {
            console.error('[VFS] Mounting failed:', error);
        }
    }

    /**
     * Generic background mounter for large content collections.
     * @private
     */
    _mountCollection(items, nodePath, kind) {
        if (!Array.isArray(items)) return;

        items.forEach(item => {
            const filename = item.id; 
            const fullPath = `${nodePath}/${filename}`;

            // Link to directory children list if not already present
            if (!this.vfs[nodePath].children.includes(filename)) {
                this.vfs[nodePath].children.push(filename);
            }

            // ATOMIC CONTENT: Store metadata and excerpt. 
            // CatCommand.js will fetch the full 'body_html' lazily.
            this.vfs[fullPath] = {
                "type": "file",
                "content": `${item.title} [${(item.status || item.date || '').toUpperCase()}]\n${item.excerpt || ''}`, 
                "meta": {
                    "kind": kind,
                    "id": item.id,
                    "title": item.title,
                    ...(item.status && { status: item.status }),
                    ...(item.date && { date: item.date }),
                    "tags": item.tags || [],
                    "author": "user"
                }
            };
        });
    }

    /**
     * Ensures directory nodes are present and linked to the home parent.
     * @private
     */
    _ensureDirectoryNode(path, name) {
        const homePath = '/home/user';
        
        if (!this.vfs[path]) {
            this.vfs[path] = { type: 'dir', children: [] };
        }

        if (this.vfs[homePath] && !this.vfs[homePath].children.includes(name)) {
            this.vfs[homePath].children.push(name);
        }
    }

    // ========================================================
    // NAVIGATION & RESOLUTION
    // ========================================================

    /**
     * Resolves relative paths (.., .) and absolute paths into a canonical form.
     * @param {string} path 
     * @returns {string} Absolute path string
     */
    resolvePath(path) {
        if (!path) return this.currentDirectory;
        if (path === '.') return this.currentDirectory;

        let parts;
        if (path.startsWith('/')) {
            parts = path.split('/').filter(p => p !== '');
        } else {
            const currentParts = this.currentDirectory === '/' 
                ? [] 
                : this.currentDirectory.split('/').filter(p => p !== '');
            const targetParts = path.split('/').filter(p => p !== '');
            parts = currentParts.concat(targetParts);
        }

        const resolvedParts = [];
        for (const part of parts) {
            if (part === '.') continue;
            if (part === '..') {
                if (resolvedParts.length > 0) resolvedParts.pop();
            } else {
                resolvedParts.push(part);
            }
        }
        return '/' + resolvedParts.join('/');
    }

    getCurrentDirectory() { 
        return this.currentDirectory; 
    }

    /**
     * Retrieves a node from the VFS. Supports case-insensitive matching.
     * @param {string} path 
     * @returns {Object|null} The VFS node object
     */
    getNode(path) { 
        if (this.vfs[path]) return this.vfs[path];
        
        // Fallback: Case-insensitive search
        const lowerPath = path.toLowerCase();
        const key = Object.keys(this.vfs).find(k => k.toLowerCase() === lowerPath);
        return key ? this.vfs[key] : null;
    }

    /**
     * Handles directory navigation logic.
     * @param {string} pathArg 
     */
    cd(pathArg) { 
        const targetPath = this.resolvePath(pathArg);
        const targetNode = this.getNode(targetPath);

        if (targetNode?.type === 'dir') {
            this.currentDirectory = targetPath;
            return { type: 'success' };
        } else if (targetNode?.type === 'file') {
            return { type: 'error', content: `cd: not a directory: ${pathArg}` };
        } else {
            return { type: 'error', content: `cd: no such file or directory: ${pathArg}` };
        }
    }

    // ========================================================
    // CONTENT OPERATIONS
    // ========================================================

    /**
     * Simulates the 'cat' command. 
     * Returns an object that allows CatCommand.js to fetch full content ifKind is 'blog'/'project'.
     */
    cat(pathArg, otherArgs = []) {
        const targetPath = this.resolvePath(pathArg);
        const fileNode = this.getNode(targetPath);

        if (!fileNode) {
            return { type: 'error', content: `cat: ${pathArg}: No such file or directory` };
        }

        if (fileNode.type === 'dir') {
            return { type: 'error', content: `cat: ${pathArg}: Is a directory` };
        }

        // Handle CTF / Protected Files
        if (fileNode.protected) {
            if (otherArgs[0] === fileNode.code) {
                return { 
                    type: 'success', 
                    content: `--- ACCESS GRANTED ---\n${fileNode.real_content}`, 
                    className: 'text-success' 
                };
            }
            let content = fileNode.content;
            if (targetPath.includes('flag.txt')) {
                content += `\nUsage: cat ${pathArg} [access_code]`;
            }
            return { type: 'success', content: content, className: 'text-warning' };
        }

        // Standard File Return: Metadata leads the way
        return { 
            type: 'success', 
            content: fileNode.content, // Lightweight excerpt
            id: fileNode.meta?.id,
            kind: fileNode.meta?.kind, // 'project' or 'blog'
            meta: fileNode.meta 
        };
    }

    /**
     * Helper for generating valid system slugs.
     */
    slugify(text) {
        return text
            .toString()
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-');
    }
}